import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CopyButton } from '@/components/ui/copy-button';
import { monitoring_api } from '@/lib/auth';
import { ExternalConnectionDto } from '@/lib/integrationTypes/externalConnectionTypes';
import { NvrFullDto } from '@/lib/integrationTypes/ihub-devices';
import { Info } from 'lucide-react';
import { GroupedSection, GroupedSubsection } from '@/components/ui/grouped-section';
import { cn } from '@/lib/utils';

type PickKey = 'smtpUsername' | 'smtpPassword' | 'id' | 'webhookAuthCredDto';

export interface DeviceCredentialsProps {
  device: Pick<NvrFullDto, PickKey> | Pick<ExternalConnectionDto, PickKey>;
}

function InputWithCopy({
  label,
  value,
  id,
  className,
}: {
  label: string;
  value: string;
  id?: string;
  className?: string;
}) {
  return (
    <div className={cn('grid items-center gap-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input id={id} readOnly value={value} className="flex-1" />
        <CopyButton textToCopy={value} label={label} />
      </div>
    </div>
  );
}

export function SMTPCredentials({ device }: DeviceCredentialsProps) {
  return (
    <GroupedSection
      title="SMTP Credentials"
      description="Receive detections via SMTP mail servers."
    >
      <div className="mb-6 grid grid-cols-1 gap-x-4 gap-y-8 md:grid-cols-2">
        <InputWithCopy
          label="Receiver Email"
          value={(device.smtpUsername || '') + '@watchful.cloud'}
          className="col-span-2"
        />
        <InputWithCopy label="Port" value="8086" />
        <InputWithCopy label="Host" value="smtp.watchful.cloud" />
      </div>
      <GroupedSubsection title="Authentication">
        <div className="grid grid-cols-1 gap-x-4 gap-y-8 md:grid-cols-2">
          <InputWithCopy label="Username" value={device.smtpUsername || ''} />
          <InputWithCopy label="Password" value={device.smtpPassword || ''} />
        </div>
      </GroupedSubsection>
      <span className="flex items-center justify-end gap-1 text-sm text-muted/60">
        <Info className="h-4 w-4" />
        <span>Mail settings are typically found within the Network settings on the device.</span>
      </span>
    </GroupedSection>
  );
}

export function WebhookCredentials({ device }: DeviceCredentialsProps) {
  const webhookURL = `${monitoring_api}/Webhook/ihub/${device.id}/publish`;
  return (
    <GroupedSection
      title="Webhook Credentials"
      description="Receive HTTP/S webhooks from this device."
    >
      <div className="mb-8 flex w-full flex-col gap-x-4 gap-y-8 md:grid md:grid-cols-2">
        <InputWithCopy label="Webhook URL" value={webhookURL} />
        <InputWithCopy
          label="Authenticated URL"
          value={`${webhookURL}?token=${device.webhookAuthCredDto?.token || ''}`}
        />
      </div>

      <GroupedSubsection title="Bearer Auth">
        <InputWithCopy label="Token" value={`Bearer ${device.webhookAuthCredDto?.token || ''}`} />
      </GroupedSubsection>
      <GroupedSubsection title="Basic Auth">
        <div className="mt-4 grid grid-cols-2 gap-4">
          <InputWithCopy label="Username" value={device.webhookAuthCredDto?.username || ''} />
          <InputWithCopy label="Password" value={device.webhookAuthCredDto?.password || ''} />
        </div>
      </GroupedSubsection>
    </GroupedSection>
  );
}

export default function DeviceCredentials({ device }: DeviceCredentialsProps) {
  const hasSMTP = device.smtpUsername && device.smtpPassword;
  const hasWebhook = device.webhookAuthCredDto;

  if (!hasSMTP && !hasWebhook) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Device Credentials</h2>
        <p className="text-muted/60">
          These credentials are used to authenticate with the device when it sends an alarm event or
          detection.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-1 rounded-md bg-green-dark p-2">
        {hasSMTP ? <SMTPCredentials device={device} /> : null}
        {hasWebhook ? <WebhookCredentials device={device} /> : null}
      </div>
    </div>
  );
}
